<script setup lang="ts">
import { Form as VeeForm, FormContext } from 'vee-validate'

import { useBasketStore } from '@/store/basket/basket'
import { useBasketTimeStore } from '@/store/basket/basket-time'
import { useCityStore } from '@/store/city/city'

import { PaymentMethodType, SavedCard } from '@/types/api/api-v3/paymentMethods'
import { BasketStep } from '@/types/basket'

import BasketPayment from '@/components/Basket/basket-payment/BasketPayment.vue'
import BasketPricePanel from '@/components/Basket/basket-price-panel/BasketPricePanel.vue'
import BasketTime from '@/components/Basket/basket-time/BasketTime.vue'

const DialogBasketTransactionTime = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/DialogBasketTransactionTime.vue'),
)

const DialogBasketProposition = defineAsyncComponent(
  () => import('@/components/Basket/dialogs/DialogBasketProposition.vue'),
)

const EVENT_TYPE_PS = 'payment_started_payselection'
const EVENT_YANDEX_NAME = 'payment_started_SBP'

type Props = {
  clientPacksCount: number
  clientAdditivesCount: number
}

type AdditionalPaymentAction = (orderId: number) => Promise<void>

const emit = defineEmits(['scrollToErrorBlock', 'goToStep'])
defineProps<Props>()

const { $apiService, $metrics, $sentryCaptureException } = useNuxtApp()

const { currencySymbol } = useCityStore()
const basketStore = useBasketStore()
const basketTimeStore = useBasketTimeStore()

const basketObserver = ref<FormContext | null>(null)
const dialogs = ref({
  showProposition: {
    visible: false,
  },
  transactionTime: {
    visible: false,
  },
})

const isPayProcessLoading = ref(false)
const propositionShowedAlready = ref(false)

const basket = computed(() => basketStore.data)
const checkSelectedSavedCard = computed(
  () => basketStore.checkSelectedSavedCard,
)
const selectedPaymentMethod = computed(
  () => basketStore.data.selectedPaymentMethod,
)
const amountToPayWithSymbol = computed(
  () => `${basket.value.amountToPay} ${currencySymbol}`,
)

// Скролл до блока с ошибкой
const showError = () =>
  setTimeout(() => {
    const fieldError = getFieldError()

    emit('scrollToErrorBlock', fieldError)
  }, 0)

// Функция для получения id блока, в котором есть ошибка валидации
const getFieldError = () => {
  const fieldsErrors = basketObserver.value?.errors

  if (!fieldsErrors) return null

  return Object.keys(fieldsErrors).at(0) || null
}

const showPropositionDialog = (): boolean => {
  // Если точка, на которую упадет заказ extra - не показываем доборы
  if (basket.value.isExtra) return false

  // Если показывали доборы до этого - не показываем доборы
  if (propositionShowedAlready.value) return false

  if (basket.value.proposition.items.length === 0) return false

  dialogs.value.showProposition.visible = true
  propositionShowedAlready.value = true

  return true
}

const preClearanceCheckSave = async () => {
  // Переходим к оформлению заказа картой, в зависимости от того, добавлена она клиентом или нет
  checkSelectedSavedCard.value
    ? await orderCheckoutByCard()
    : await orderCheckout()
}

// Общий метод обработки оформления заказа
const handleOrderCheckout = async (
  showTransactionDialog: boolean,
  additionalPaymentAction: AdditionalPaymentAction | null,
) => {
  // Показываем доборы
  const isPropositionDialogShowed = showPropositionDialog()

  if (isPropositionDialogShowed) return

  // Если оплата картой - показываем экран загрузки
  if (showTransactionDialog) dialogs.value.transactionTime.visible = true

  isPayProcessLoading.value = true

  const orderId = await basketStore.orderCheckout()

  if (basketStore.error) {
    dialogs.value.transactionTime.visible = false
    isPayProcessLoading.value = false

    return
  }

  if (additionalPaymentAction) {
    await additionalPaymentAction(orderId)
  } else {
    if (selectedPaymentMethod.value?.type === PaymentMethodType.SBP)
      $metrics?.eventSendGoal({ ym: EVENT_YANDEX_NAME })

    if (selectedPaymentMethod.value?.type === PaymentMethodType.PAY_SELECTION)
      $metrics?.eventSendGoal({ ym: EVENT_TYPE_PS, mt: EVENT_TYPE_PS })
  }

  redirectToOrderPage(orderId)
}

const orderCheckout = async () => await handleOrderCheckout(false, null)

// Метод оплаты по сохраненной карте
const orderCheckoutByCard = async () => {
  await handleOrderCheckout(true, async (orderId: number) => {
    const paymentMethodId = (selectedPaymentMethod.value as SavedCard)?.id

    // Если нет id метода оплаты - выходим без оплаты
    if (!paymentMethodId) {
      $sentryCaptureException(`Не удалось найти id оплаты: ${paymentMethodId}`)

      return
    }

    await $apiService.order.paySavedCardPaySelection(
      String(orderId),
      String(paymentMethodId),
    )
  })
}

const closePropositionDialog = async (isError: boolean) => {
  dialogs.value.showProposition.visible = false

  // Если при добавлении добора ошибка - обновляем корзину
  if (isError) {
    propositionShowedAlready.value = false

    await basketStore.fetchBasket()

    return
  }

  checkSelectedSavedCard.value
    ? await orderCheckoutByCard()
    : await orderCheckout()
}

const redirectToOrderPage = (orderId: string) => {
  localStorage.setItem('showedWarningAdditivesOrClientPack', 'false')

  navigateTo(`/profile/orders/${orderId}/`)
}

const goToPrevStep = () => emit('goToStep', BasketStep.FIRST)
</script>

<template>
  <vee-form
    v-slot="{ handleSubmit }"
    ref="basketObserver"
    class="basket__section"
    validate-on-mount
  >
    <basket-time
      v-if="basketStore.basketAddressSelected"
      class="section delivery"
      @go-to-prev-step="goToPrevStep"
    />

    <basket-payment v-if="!basketTimeStore.error" class="section" />

    <basket-price-panel
      class="section"
      :amount-to-pay-with-symbol="amountToPayWithSymbol"
      :basket-step="BasketStep.SECOND"
      :is-price-section-sticky="false"
      :is-loading="isPayProcessLoading"
      @order-checkout="handleSubmit(preClearanceCheckSave), showError()"
    />
  </vee-form>

  <!-- Диалоговое окно обработки платежа -->
  <the-dialog
    :max-width="360"
    :model-value="dialogs.transactionTime.visible"
    padding="0"
    persistent
  >
    <dialog-basket-transaction-time
      v-click-outside="false"
      class="transaction-dialog"
    />
  </the-dialog>

  <!-- Диалоговое окно доборов -->
  <dialog-basket-proposition
    v-if="dialogs.showProposition.visible"
    :propositions="basket.proposition.items"
    :time-to-live="basket.proposition.timeToLive"
    @close="closePropositionDialog"
  />
</template>

<style lang="scss" scoped>
@use '@/assets/styles/basket' as *;
</style>
